import { routePlan } from "../data/routePlan.ts";
import { crewCoverage } from "../data/crewCoverage.ts";
import { ZONES, zoneAt } from "../data/map.ts";
import { SPECIES } from "../data/species.ts";
import { TRAINERS } from "../data/trainers.ts";
import { weekOf } from "../game/goals.ts";
import { TYPE_META, typeMod } from "../data/types.ts";
import { enterAge } from "../art/craftFeel.ts";
import { creatureSprite } from "../art/creatures.ts";
import { UI, withA, type Col } from "../art/pal.ts";
import { measureText } from "../engine/font.ts";
import { easeOut, SCREEN_H, SCREEN_W } from "../engine/gfx.ts";
import { PARTY_MAX } from "../game/save.ts";
import type { Ctx } from "../game/ctx.ts";
import type { Scene } from "../game/scenes.ts";
import { backdrop, glass, hint } from "../ui/kit.ts";
import { actorFrame, TRAINER_OUTFITS } from "../world/actors.ts";

const TABS = ["TRAIN", "DUEL", "CREW", "NOTES"];
const TAB_COL: Col[] = [UI.green, UI.pink, UI.teal, UI.gold];

export function routePlanScene(ctx: Ctx): Scene {
  let page = 0;
  let age = enterAge(ctx.shot);
  return {
    id: "plan",
    native: true,
    enter() { page = 0; age = enterAge(ctx.shot); },
    exit() {},
    update() {
      age += ctx.dt;
      if (ctx.input.consume("Escape")) { ctx.audio.back(); ctx.pop(); return; }
      const d = ctx.input.menuDir(ctx.now);
      if (ctx.input.consume("Enter") || d?.x || d?.y) {
        page = (page + (d && (d.x < 0 || d.y < 0) ? -1 : 1) + 4) % 4;
        age = ctx.shot ? age : 0;
        ctx.audio.cursor();
      }
    },
    draw() {
      const g = ctx.g, state = { ...ctx.state, week: weekOf(ctx.today()) }, plan = routePlan(state);
      const reduced = ctx.reduced;
      backdrop(g, ctx.now, reduced, [22, 40, 44, 255], [10, 12, 24, 255]);
      // Tabs.
      g.textShadow(10, 8, `FIELD PLAN / ${TABS[page]}`, UI.text, UI.ink);
      TABS.forEach((tab, n) => {
        const w = measureText(tab) + 18;
        const x = 150 + n * 58;
        const on = n === page;
        g.rrect(x, 4, 54, 16, 7, on ? TAB_COL[n]! : withA(UI.bg2, 220));
        g.textC(x + 27, 9, tab, on ? UI.paperInk : UI.textDim);
        void w;
      });
      const k = reduced ? 1 : easeOut(Math.min(1, age / 200));
      const cx = 8, cy = 26 + Math.round((1 - k) * 12), cw = SCREEN_W - 16, ch = 168;
      glass(g, cx, cy, cw, ch, { r: 8, border: TAB_COL[page]! });
      let y = cy + 10;
      const row = (text: string, color: Col = UI.text, x = cx + 12) => { g.textShadow(x, y, text, color, UI.ink); y += 13; };
      const gap = (n = 5) => { y += n; };
      if (page === 0) {
        row(`HERE: ${ZONES[plan.zone].name}`, UI.goldHi);
        row(plan.local ? `Local wilds: Lv${plan.local.lo}-${plan.local.hi}` : "Safe town. Rest and stock up.", UI.textDim);
        gap();
        row(plan.lead ? `LEAD ${plan.lead.nick.slice(0, 12)} Lv${plan.lead.level}` : "No living lead. Visit the clinic.");
        g.para(cx + 12, y, plan.advice, 220, UI.teal, 11, 3); y += 26;
        row(plan.ready ? `Ready: ${plan.ready.nick.slice(0, 12)} Lv${plan.ready.level}` : "Free kettles restore HP and PP.", UI.textDim);
        gap();
        row(`TRY: ${ZONES[plan.training.zone].name}`, UI.goldHi);
        row(`${plan.training.region === "all" ? "Grass" : `${plan.training.region === "north" ? "North" : "South"} grass`}: Lv${plan.training.lo}-${plan.training.hi}`);
        row(`Rest: ${plan.kettle.title}`, UI.textDim);
        row("CREW L/R: choose your lead.", UI.textMute);
        if (plan.lead) {
          g.disc(cx + cw - 56, cy + 60, 34, withA(UI.green, 30));
          g.blit(creatureSprite(plan.lead.speciesId, "front", 2, !!plan.lead.shiny), cx + cw - 88, cy + 26);
        }
      } else if (page === 1) {
        row(plan.repeat ? `REPEAT: ${plan.next.name}` : `NEXT: ${plan.next.name}`, UI.goldHi);
        row(ZONES[zoneAt(plan.next.x, plan.next.y)].name, UI.textDim);
        const size = plan.next.team.length;
        row(plan.next.lineup ?? `${["One", "Two", "Three", "Four", "Five", "Six"][size - 1]}-partner ${plan.next.boss ? "trial" : "duel"}`, UI.teal);
        gap(3);
        // Up to six partners: tighter rows keep the card inside the frame.
        const tight = size > 4;
        plan.next.team.forEach((m, i) => {
          g.rrect(cx + 10, y - 3, 200, tight ? 12 : 14, 5, withA(UI.bg3, 160));
          row(`${i + 1}. ${SPECIES[m.species].name} Lv${m.level}`, UI.text, cx + 16);
          y += tight ? -1 : 2;
        });
        gap(3);
        row(plan.practice ? `Practice: ${TRAINERS[plan.practice].name}` : "Kettles are free between duels.", UI.textDim);
        row("Bring mixed types. And a sip.", UI.textDim);
        if (!tight) row("In battle: B opens free tactics.", UI.textMute);
        const portrait = actorFrame(`tr:${plan.next.id}`, TRAINER_OUTFITS[plan.next.id], 1, 0);
        g.disc(cx + cw - 56, cy + 70, 40, withA(UI.pink, 30));
        g.blit(portrait, cx + cw - 80, cy + 22, { scale: 3 });
      } else if (page === 2) {
        const coverage = crewCoverage(state), risk = coverage.risk, option = coverage.option;
        row(`NEXT: ${coverage.next.name}`, UI.goldHi);
        row(`CREW ${coverage.total}/${PARTY_MAX} / ${coverage.living} living`, UI.textDim);
        gap();
        if (risk) {
          const type = TYPE_META[risk.type].name;
          row(`Watch ${type}: ${risk.count}/${coverage.living} weak`, UI.pink);
          row("This type writes the invoice.", UI.textDim);
          gap();
          if (option?.kind === "crew" || option?.kind === "box") {
            row(`${option.kind === "box" ? "BOX" : "TYPE COVER"}: ${option.mon.nick.slice(0, 12)} Lv${option.mon.level}`, UI.teal);
            row(`${type} damage x${typeMod(risk.type, option.species.types)}`);
            row(option.kind === "box" ? "Visit a Meme Box by a clinic." : "Check its HP, PP and other matchups.", UI.textDim);
          } else if (option?.kind === "evolve") {
            row(`Grow ${option.species.name} to Lv${option.species.evolveLevel}.`, UI.teal);
            row(`${option.evolved.name}: ${type} x${typeMod(risk.type, option.evolved.types)}`);
            row("Living reserves also earn EXP.", UI.textDim);
          } else if (option?.kind === "seen") {
            row(`SEEN OPTION: ${option.species.name}`, UI.teal);
            row(`${ZONES[option.habitat.zone].name} Lv${option.habitat.lo}-${option.habitat.hi}`);
            row("Index: habitat and move kit.", UI.textDim);
          } else {
            row("Seek a different type in the wild.", UI.teal);
            row("Index records seen kits/habitats.");
            row("Weakening helps tins recruit.", UI.textDim);
          }
          gap();
          row("Levels, HP and moves still matter.", UI.textMute);
        } else {
          row(coverage.living < 2 ? "Rest or recruit more partners." : "No shared weakness in this kit.", UI.green);
          row("Check individual matchups in battle.");
          row("B opens free tactics on your turn.", UI.textDim);
          row("Types alone do not promise a win.", UI.textMute);
        }
      } else {
        ["Lead with a healthy, ready partner.", "Living reserves also earn EXP.", "Fainted reserves earn no EXP.", "Train below grass that bites back.", "Three partners can already duel.", "Weak wilds are easier to tin.", "Kettles restore the whole crew.", "Trainer RUN: preview walking home.", "Hold SHIFT (or RUN) to jog.", "Gamepads work: A / B / Start."].forEach((s, i) => {
          g.disc(cx + 16, y + 3, 2, TAB_COL[i % 4]!);
          row(s, i % 2 ? UI.textDim : UI.text, cx + 24);
          void i;
        });
      }
      hint(g, 10, SCREEN_H - 18, "A", `page ${page + 1}/4`, "a");
      hint(g, 90, SCREEN_H - 18, "B", "back", "b");
    },
  };
}
